import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { loadCompanyDataAsync } from "store/actions/authActions";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { ThunkDispatch } from "redux-thunk";
//#endregion
//#region types
import { SettingsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { MessageBubbleDetails } from "models/propTypes/messagePropTypes";
import { StyleEnums } from "models/enums";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormFieldConstants, FeatureConstants } from "utils/constants";
import { InvoiceSettingsFormContainerProps } from "models/propTypes/settingsPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik/dist/types";
//#endregion
//#region components
import InvoiceSettingsForm from "./invoiceSettingsForm";
import { Skeletons, useMountedState } from "components/common";
import { useSettingsService, useFeatureService, useInvoiceService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const InvoiceSettingsFormContainer: ForwardRefRenderFunction<FormHandles, InvoiceSettingsFormContainerProps> = (
  props,
  ref
) => {
  const settingsService = useSettingsService();
  const invoiceService = useInvoiceService();
  const featureService = useFeatureService();
  const [defaultValues, setDefaultValues] = React.useState<SettingsFormTypes.CustomerInvoiceSettings>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isAutomaticRemindersFeatureEnabled, setIsAutomaticRemindersFeatureEnabled] = React.useState(false);
  const selectedCompanyId = useSelector((state: AppState) => state.auth.selectedCompany.id);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { getMounted } = useMountedState();

  const validationSchema = Yup.object<SettingsFormTypes.CustomerInvoiceSettings>({
    defaultPaymentTerms: Yup.number().required(TranslationKeys.Validations.Required),
    invoiceIBAN: Yup.string(),
    invoiceBIC: Yup.string(),
    companyEmail: Yup.string()
      .email(TranslationKeys.Validations.InvalidEmail)
      .when(
        FormFieldConstants.Settings.CustomerInvoiceSettings.SendInvoiceFromId,
        ([sendInvoiceFromId], schema: Yup.StringSchema) =>
          sendInvoiceFromId === WintSuperkollAPIModel.SendInvoiceMailsFromEnum.CompanyMail
            ? schema.required(TranslationKeys.Validations.Required)
            : schema
      ),
    sendInvoiceFromName: Yup.string(),
    sendInvoiceFromId: Yup.number(),
    invoiceTitleTemplate: Yup.string(),
    invoiceMessageTemplate: Yup.string(),
    invoiceFileNameTemplate: Yup.string().required(TranslationKeys.Validations.Required),
    defaultInvoiceLayout: Yup.number(),
    defaultNotes: Yup.string(),
    homePageUrl: Yup.string(),
    hideDeliveryDate: Yup.boolean(),
    useFTax: Yup.boolean(),
    units: Yup.array()
      .min(1, TranslationKeys.Validations.AtLeastOneItem)
      .of(
        Yup.object<SettingsFormTypes.InvoiceUnit>({
          name: Yup.string()
            .required(TranslationKeys.Validations.Required)
            .max(20, TranslationKeys.Validations.ValueTooLong),
        })
      ),
    invoicePaymentOptions: Yup.array()
      .min(1, TranslationKeys.Validations.AtLeastOneItem)
      // Invoices resolve their payment method and number through the option flagged
      // isDefault, so a list without exactly one is not saveable. The message lands on
      // the Betalsätt field itself (useFormFieldMessage renders string errors on a
      // touched field); the submit toast is generic for any Yup failure.
      .test(
        "exactlyOneDefault",
        TranslationKeys.Validations.OnePaymentOptionMustBeDefault,
        (options?: SettingsFormTypes.PaymentOption[]) =>
          !options?.length || options.filter((option) => option?.isDefault).length === 1
      )
      .of(
        Yup.object<SettingsFormTypes.PaymentOption>({
          id: Yup.number(),
          methodId: Yup.number(),
          methodName: Yup.string().required(TranslationKeys.Validations.Required),
          isDefault: Yup.boolean(),
          number: Yup.string().when(
            FormFieldConstants.Settings.PaymentOption.MethodId,
            ([paymentMethod], schema: Yup.StringSchema) => {
              return paymentMethod === WintSuperkollAPIModel.InvoicePaymentMethod.Autogiro
                ? schema.notRequired().oneOf(["", undefined], TranslationKeys.Validations.DirectDebitCantHaveAccount)
                : schema.required(TranslationKeys.Validations.Required);
            }
          ),
        })
      ),
    invoiceSendType: Yup.number(),
    invoiceSendTypeName: Yup.string(),
    reminderCost: Yup.number().required(TranslationKeys.Validations.Required),
    penaltyInterestType: Yup.number().required(TranslationKeys.Validations.Required),
    penaltyInterestPercentage: Yup.number().max(
      100,
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Max, "", { max: "100" })
    ),
    isAutomaticRemindersEnabled: Yup.number().required(TranslationKeys.Validations.Required),
    automaticReminderDelay: Yup.number(),
    automaticReminderAmountLimit: Yup.number(),
    automaticReminderPeriod: Yup.number()
      .required(TranslationKeys.Validations.Required)
      .when(
        FormFieldConstants.Settings.CustomerInvoiceSettings.AutomaticReminderDelay,
        ([automaticReminderDelay], schema: Yup.NumberSchema) => {
          const minValue = automaticReminderDelay ? Math.max(5, automaticReminderDelay + 1) : 5;
          return schema.min(
            minValue,
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", { min: `${minValue}` })
          );
        }
      ),
    automaticReminderCountLimit: Yup.number(),
    invoiceReminderNotesTemplate: Yup.string().required(TranslationKeys.Validations.Required),
    invoiceReminderTitleTemplate: Yup.string().required(TranslationKeys.Validations.Required),
    invoiceReminderMessageTemplate: Yup.string().required(TranslationKeys.Validations.Required),
    invoiceReminderFileNameTemplate: Yup.string().required(TranslationKeys.Validations.Required),
    invoiceReminderSendType: Yup.number(),
    invoiceReminderSendTypeName: Yup.string(),
  });

  const handleSubmit = (values: SettingsFormTypes.CustomerInvoiceSettings) => {
    const invoicePost: WintSuperkollAPIModel.InvoiceSettingsPut = {
      defaultPaymentTerms: values.defaultPaymentTerms ?? 0,
      invoicePaymentOptions: values.invoicePaymentOptions?.map((option) => {
        return {
          id: option.id === undefined || UtilsHelper.isClientGeneratedId(option.id) ? 0 : option.id,
          method: option.methodId,
          number: UtilsHelper.isEmpty(option.number) ? undefined : option.number,
          isDefault: option.isDefault,
        } as WintSuperkollAPIModel.InvoicePaymentOption;
      }),
      invoiceIBAN: values.invoiceIBAN,
      invoiceBIC: values.invoiceBIC,
      sendMailsFrom: values.sendInvoiceFromId ?? WintSuperkollAPIModel.SendInvoiceMailsFromEnum.PersonMail,
      companyMail: values.companyEmail,
      invoiceTitleTemplate: values.invoiceTitleTemplate,
      invoiceMessageTemplate: values.invoiceMessageTemplate,
      invoiceFileNameTemplate: values.invoiceFileNameTemplate,
      invoiceReminderTitleTemplate: values.invoiceReminderTitleTemplate,
      invoiceReminderMessageTemplate: values.invoiceReminderMessageTemplate,
      invoiceReminderFileNameTemplate: values.invoiceReminderFileNameTemplate,
      defaultPdfTemplate: values.defaultInvoiceLayout ?? WintSuperkollAPIModel.InvoicePdfTemplate.Default,
      defaultNotes: values.defaultNotes,
      defaultReminderNotes: values.invoiceReminderNotesTemplate,
      homePageUrl: values.homePageUrl,
      useFTax: values.useFTax,
      hideDeliveryDate: values.hideDeliveryDate,
      unitLines: values.units as WintSuperkollAPIModel.Unit[],
      defaultInvoiceSendOption: values.invoiceSendType,
      reminderCost: values.reminderCost ?? 0,
      penaltyInterest: {
        type: values.penaltyInterestType,
        rate: values.penaltyInterestPercentage / 100,
      },
      isAutomaticRemindersEnabled: values.isAutomaticRemindersEnabled === 1,
      automaticReminderPeriod: values.automaticReminderPeriod,
      automaticReminderDelay: values.automaticReminderDelay,
      automaticReminderAmountLimit: values.automaticReminderAmountLimit,
      automaticReminderCountLimit: values.automaticReminderCountLimit,
      defaultInvoiceReminderSendOption: values.invoiceReminderSendType,
    };
    return settingsService.updateCustomerInvoiceSettings(invoicePost).then((res) => {
      if (getMounted()) {
        setDefaultValues(values);
      }
      dispatch(loadCompanyDataAsync(selectedCompanyId));
    });
  };

  const onBICOptionSelect = (item: AutocompleteListItem, name: string, formikProps: FormikProps<any>) => {
    const { setFieldValue } = formikProps;
    item.id && setFieldValue(FormFieldConstants.Settings.CustomerInvoiceSettings.InvoiceBIC, item.id);
  };

  const onPaymentOptionSetAsDefault = async (
    rowIndex: number,
    row: SettingsFormTypes.PaymentOption,
    formikProps: FormikProps<SettingsFormTypes.CustomerInvoiceSettings>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;

    const updatedPaymentMethods = values.invoicePaymentOptions?.map((paymentOption, index) => ({
      ...paymentOption,
      isDefault: rowIndex === index,
    }));

    setFieldValue(FormFieldConstants.Settings.CustomerInvoiceSettings.InvoicePaymentOptions, updatedPaymentMethods);
    setFieldTouched(FormFieldConstants.Settings.CustomerInvoiceSettings.InvoicePaymentOptions, true);
  };

  const onPaymentOptionMethodChange = (
    name: string,
    value: string,
    formikProps: FormikProps<SettingsFormTypes.CustomerInvoiceSettings>
  ) => {
    const { values, setFieldValue } = formikProps;
    const options = values.invoicePaymentOptions;

    // A payment option list is only meaningful with exactly one default: invoices
    // resolve their payment method and number through it. When the very first option
    // is created there is nothing to choose between, so mark it as the default right
    // away rather than asking the user to find a menu item for it.
    //
    // Scoped to a row the user is creating right now (no server id yet), so this never
    // rewrites stored settings behind the user's back. Companies already stored without
    // a default are repaired by the API, which is the only writer.
    if (UtilsHelper.isEmpty(value) || options?.length !== 1) {
      return;
    }

    const [option] = options;
    const isNewOption = option.id === undefined || UtilsHelper.isClientGeneratedId(option.id);

    if (option.isDefault || !isNewOption) {
      return;
    }

    // Deliberately no setFieldTouched: this is the app filling in a value the user did
    // not type, so it must not mark the list as edited. Touching it here also risks
    // surfacing the row's own required-field errors before the user has reached them.
    setFieldValue(FormFieldConstants.Settings.CustomerInvoiceSettings.InvoicePaymentOptions, [
      { ...option, isDefault: true },
    ]);
  };

  const onPaymentOptionDelete = async (
    rowIndex: number,
    row: SettingsFormTypes.PaymentOption,
    formikProps: FormikProps<SettingsFormTypes.CustomerInvoiceSettings>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;

    const updatedPaymentMethods = values.invoicePaymentOptions ? [...values.invoicePaymentOptions] : [];
    updatedPaymentMethods.splice(rowIndex, 1);

    setFieldValue(FormFieldConstants.Settings.CustomerInvoiceSettings.InvoicePaymentOptions, updatedPaymentMethods);
    setFieldTouched(FormFieldConstants.Settings.CustomerInvoiceSettings.InvoicePaymentOptions);
  };

  const [dynamicMessage, SetDynamicMessage] = React.useState<string>("");

  const fromMailCustomMessage = async (values: SettingsFormTypes.CustomerInvoiceSettings) => {
    let message: MessageBubbleDetails | undefined;
    if (values.sendInvoiceFromName) {
      if (values.sendInvoiceFromId === WintSuperkollAPIModel.SendInvoiceMailsFromEnum.Superkoll) {
        setTimeout(() => {
          SetDynamicMessage("");
        }, 3000);
        return {
          type: StyleEnums.DisplayType.Success,
          message: dynamicMessage,
        };
      } else {
        SetDynamicMessage(LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MailSendFromSuccess));
        return {
          type: StyleEnums.DisplayType.Warning,
          message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MailSendFromWarning),
        };
      }
    } else {
      message = undefined;
    }
    return message;
  };

  React.useEffect(() => {
    Promise.all([
      settingsService.getCustomerInvoiceSettings(),
      featureService.hasFeature(FeatureConstants.AutomaticReminders),
    ]).then(([invoiceSettings, isAutomaticRemindersFeatureEnabled]) => {
      if (getMounted()) {
        setDefaultValues({
          defaultPaymentTerms: invoiceSettings?.defaultPaymentTerms,
          invoiceIBAN: invoiceSettings?.invoiceIBAN,
          invoiceBIC: invoiceSettings?.invoiceBIC,
          companyEmail: invoiceSettings?.companyMail,
          sendInvoiceFromId: invoiceSettings?.sendMailsFrom,
          sendInvoiceFromName: LocalizationHelperInstance.getFormattedMessage(
            `Settings.SendInvoiceFrom_${invoiceSettings?.sendMailsFrom}`
          ),
          invoiceTitleTemplate: invoiceSettings?.invoiceTitleTemplate,
          invoiceMessageTemplate: invoiceSettings?.invoiceMessageTemplate,
          invoiceFileNameTemplate: invoiceSettings?.invoiceFileNameTemplate,
          defaultInvoiceLayout: invoiceSettings?.defaultPdfTemplate,
          homePageUrl: invoiceSettings?.homePageUrl,
          defaultNotes: invoiceSettings?.defaultNotes,
          useFTax: invoiceSettings?.useFTax,
          hideDeliveryDate: invoiceSettings?.hideDeliveryDate,
          units: invoiceSettings?.unitLines,
          invoicePaymentOptions: invoiceSettings?.invoicePaymentOptions?.map((option) => {
            return {
              id: option.id,
              methodId: option.method,
              methodName: LocalizationHelperInstance.getFormattedMessage(`Invoice.PaymentMethods_${option.method}`),
              number: option.number,
              isDefault: option.isDefault,
            } as SettingsFormTypes.PaymentOption;
          }),
          invoiceSendType: invoiceSettings?.defaultInvoiceSendOption,
          invoiceSendTypeName:
            invoiceSettings?.defaultInvoiceSendOption !== undefined
              ? invoiceService.getSendAsTypeText(invoiceSettings.defaultInvoiceSendOption)
              : undefined,
          reminderCost: invoiceSettings?.reminderCost,
          penaltyInterestType:
            invoiceSettings?.penaltyInterest?.type ??
            WintSuperkollAPIModel.InvoicePenaltyInterestType.NoPenaltyInterest,
          penaltyInterestPercentage:
            invoiceSettings?.penaltyInterest?.rate !== undefined ? invoiceSettings.penaltyInterest.rate * 100 : 0,
          isAutomaticRemindersEnabled: invoiceSettings?.isAutomaticRemindersEnabled ?? false ? 1 : 0,
          automaticReminderAmountLimit: invoiceSettings?.automaticReminderAmountLimit,
          automaticReminderPeriod: invoiceSettings?.automaticReminderPeriod,
          automaticReminderDelay: invoiceSettings?.automaticReminderDelay,
          automaticReminderCountLimit: invoiceSettings?.automaticReminderCountLimit,
          invoiceReminderNotesTemplate: invoiceSettings?.defaultReminderNotes,
          invoiceReminderTitleTemplate: invoiceSettings?.invoiceReminderTitleTemplate,
          invoiceReminderMessageTemplate: invoiceSettings?.invoiceReminderMessageTemplate,
          invoiceReminderFileNameTemplate: invoiceSettings?.invoiceReminderFileNameTemplate,
          invoiceReminderSendType: invoiceSettings?.defaultInvoiceReminderSendOption,
          invoiceReminderSendTypeName:
            invoiceSettings?.defaultInvoiceReminderSendOption !== undefined
              ? LocalizationHelperInstance.getFormattedMessage(
                  `Invoice.AutomaticReminderSendType_${invoiceSettings.defaultInvoiceReminderSendOption}`
                )
              : undefined,
        });
        setIsAutomaticRemindersFeatureEnabled(isAutomaticRemindersFeatureEnabled);
        setLoading(false);
      }
    });
  }, []);

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <InvoiceSettingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      defaultValues={defaultValues}
      onBICOptionSelect={onBICOptionSelect}
      onPaymentOptionSetAsDefault={onPaymentOptionSetAsDefault}
      onPaymentOptionDelete={onPaymentOptionDelete}
      onPaymentOptionMethodChange={onPaymentOptionMethodChange}
      fromMailCustomMessageHandler={fromMailCustomMessage}
      isAutomaticRemindersFeatureEnabled={isAutomaticRemindersFeatureEnabled}
    />
  );
};

export default forwardRef(InvoiceSettingsFormContainer);
